import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, RotateCw, X } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { analyzeReceipt } from '@/lib/openai';
import TransactionReview from './TransactionReview';

interface FullScreenCameraProps {
  onClose: () => void;
  onPhotoTaken: (data: any) => Promise<void>;
}

export const FullScreenCamera: React.FC<FullScreenCameraProps> = ({ onClose, onPhotoTaken }) => {
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [transaction, setTransaction] = useState<any>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [isFrontCamera, setIsFrontCamera] = useState(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [isFrontCamera]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: isFrontCamera ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
          toast({
            title: 'Camera Error',
            description: 'Failed to start camera preview',
            variant: 'destructive'
          });
        });
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: 'Camera Error',
        description: 'Failed to access camera. Please check your camera permissions.',
        variant: 'destructive'
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const switchCamera = () => {
    setIsFrontCamera(!isFrontCamera);
  };

  const takePhoto = async () => {
    if (!videoRef.current || !streamRef.current) return;

    setIsLoading(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      ctx.drawImage(videoRef.current, 0, 0);
      const file = await new Promise<File>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], 'receipt.jpg', { type: 'image/jpeg' }));
          }
        }, 'image/jpeg', 0.9);
      });

      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = (reader.result as string).split(',')[1];
        try {
          const result = await analyzeReceipt(base64Image);
          console.log('Receipt analysis result:', result);
          setTransaction(result);
          setShowReview(true);
          stopCamera();
        } catch (error) {
          console.error('Error analyzing receipt:', error);
          toast({
            title: 'Analysis Error',
            description: 'Failed to analyze receipt. Please try again.',
            variant: 'destructive'
          });
          setTransaction({
            amount: 0,
            date: new Date().toISOString().split('T')[0],
            place: '',
            items: [],
            category: 'Other'
          });
          setShowReview(true);
          stopCamera();
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error taking photo:', error);
      toast({
        title: 'Camera Error',
        description: 'Failed to capture photo. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      await onPhotoTaken(data);
      onClose();
    } catch (error) {
      console.error('Error submitting transaction:', error);
      toast({
        title: 'Submission Error',
        description: 'Failed to submit transaction. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black/50 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/10"
        >
          <X className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={switchCamera}
          className="text-white hover:bg-white/10"
        >
          <RotateCw className="h-6 w-6" />
        </Button>
      </div>

      <div className="flex-1 relative">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          muted
        />
        {showReview && transaction && (
          <div className="absolute inset-0 bg-black/90 p-4 overflow-y-auto">
            <TransactionReview
              photo={photo}
              transaction={transaction}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowReview(false);
                setPhoto(null);
                startCamera();
              }}
            />
          </div>
        )}
      </div>

      <div className="p-4 bg-black/50 flex justify-center">
        <Button
          onClick={takePhoto}
          disabled={isLoading}
          className="w-16 h-16 rounded-full bg-white text-black hover:bg-white/90"
        >
          <Camera className="h-8 w-8" />
        </Button>
      </div>
    </div>
  );
};

export default FullScreenCamera; 